import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotifications, notificationTitle } from "@/hooks/useNotifications";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

export function NotificationBell({ className }: { className?: string }) {
  const { user } = useAuth();
  const { t } = useT();
  const { items, unread, markAllRead, markRead } = useNotifications(20);

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${className ?? ""}`}
          aria-label={t("messaging.notifications")}
        >
          <Bell className="size-5" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold">{t("messaging.notifications")}</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => void markAllRead()}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck className="size-3.5" /> {t("messaging.markAllRead")}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("messaging.noNotifications")}
            </p>
          )}
          {items.map((n) => (
            <a
              key={n.id}
              href={n.url ?? "/app/notifications"}
              onClick={() => void markRead(n.id)}
              className={`block border-b border-border/60 px-3 py-2.5 text-sm transition-colors hover:bg-muted ${
                n.read_at ? "" : "bg-primary/5"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium leading-snug">{notificationTitle(n, t)}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {timeAgo(n.created_at)}
                </span>
              </div>
              {n.body && (
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
              )}
            </a>
          ))}
        </div>
        <Link
          to="/app/notifications"
          className="block border-t border-border px-3 py-2 text-center text-xs font-semibold text-primary hover:bg-muted"
        >
          {t("messaging.seeAll")}
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
